import { ExternalIntegrationEnum, ExternalIntegrationWithMetrics } from 'api/clients/ExternalIntegrationsClient'
import { MetricSnapshot } from 'hooks/queries/externalIntegrations/types'
import { isNumber, round } from 'lodash'

export const getMetricSnapshot = (
  integration: ExternalIntegrationWithMetrics,
  metricName: string,
): MetricSnapshot | undefined => {
  return integration.latest_metric_snapshots.find((snapshot) => snapshot.metric_name === metricName)
}

export const getPercentChange = (snapshot: MetricSnapshot | undefined, period: string): number | undefined => {
  if (!snapshot) {
    return
  }
  return snapshot.metric_diffs.find((diff) => diff.diff_type === 'percentage' && diff.period === period)?.diff_value
}

export const withHandlePrefix = (name: string, provider: ExternalIntegrationEnum) => {
  if ([ExternalIntegrationEnum.X, ExternalIntegrationEnum.Instagram].includes(provider)) {
    return `@${name}`
  }
  return name
}

export const formatValue = (value: number | string, decimalPlaces = 2) => {
  const unroundedNumericValue = isNumber(value) ? value : parseFloat(value.toString())
  const numericValue = round(unroundedNumericValue, decimalPlaces)

  if (numericValue < 1000) {
    return numericValue.toString()
  }

  if (numericValue >= 1000 && numericValue < 10000) {
    // adds commas, e.g., 1,234
    return numericValue.toLocaleString()
  }

  if (numericValue >= 10000 && numericValue < 1000000) {
    // add K for thousands
    const kValue = numericValue / 1000
    const roundedKValue = Math.floor(kValue * 10) / 10
    return roundedKValue % 1 === 0 ? `${roundedKValue.toFixed(0)}k` : `${roundedKValue.toFixed(1)}k`
  }

  if (numericValue >= 1000000) {
    // add M for millions
    const mValue = numericValue / 1000000
    const roundedMValue = Math.round(mValue * 100) / 100
    return roundedMValue % 1 === 0 ? `${roundedMValue.toFixed(0)}M` : `${roundedMValue.toFixed(1)}M`
  }

  return numericValue.toString() // Fallback
}

export const formatPercentChange = (percentChange?: number) => {
  if (!percentChange) return null
  const formattedChange = percentChange > 0 ? `+${percentChange * 100}` : (percentChange * 100).toString()
  return `${formattedChange}%`
}

export const timeSavedInHours = (timeSavedInSeconds?: number) => {
  if (!timeSavedInSeconds) return 0
  return Math.round(timeSavedInSeconds / 3600)
}
